/**
 * UserSearch Component
 *
 * Autocomplete search input for finding and selecting users.
 * Shows user details (name, email, department, location) in results.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import type { RequesterInfo } from '../../types/ticket';
import { useUserSearch } from '../../hooks';
import './UserSearch.css';

interface UserSearchProps {
  /** Currently selected user */
  value: RequesterInfo | null;
  /** Called when a user is selected or cleared */
  onChange: (user: RequesterInfo | null) => void;
  /** Label for the input */
  label?: string;
  /** Placeholder text */
  placeholder?: string;
  /** Error message */
  error?: string;
  /** Disable the input */
  disabled?: boolean;
  /** Required field indicator */
  required?: boolean;
}

export const UserSearch: React.FC<UserSearchProps> = ({
  value,
  onChange,
  label = 'Search User',
  placeholder = 'Start typing to search...',
  error,
  disabled = false,
  required = false,
}) => {
  const { query, setQuery, results, isLoading, clear } = useUserSearch({
    debounceMs: 300,
    minChars: 2,
    limit: 8,
  });

  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Update highlighted index when results change
  useEffect(() => {
    setHighlightedIndex(results.length > 0 ? 0 : -1);
  }, [results]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newQuery = e.target.value;
      setQuery(newQuery);
      setIsOpen(true);

      // Clear selection if user starts typing when something is selected
      if (value) {
        onChange(null);
      }
    },
    [setQuery, value, onChange]
  );

  const handleInputFocus = useCallback(() => {
    if (query.length >= 2 && results.length > 0) {
      setIsOpen(true);
    }
  }, [query, results]);

  const handleSelectUser = useCallback(
    (user: RequesterInfo) => {
      onChange(user);
      setIsOpen(false);
      clear();
    },
    [onChange, clear]
  );

  const handleClear = useCallback(() => {
    onChange(null);
    clear();
    inputRef.current?.focus();
  }, [onChange, clear]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!isOpen || results.length === 0) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightedIndex((prev) => (prev < results.length - 1 ? prev + 1 : 0));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : results.length - 1));
          break;
        case 'Enter':
          e.preventDefault();
          if (highlightedIndex >= 0 && highlightedIndex < results.length) {
            handleSelectUser(results[highlightedIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          setIsOpen(false);
          break;
      }
    },
    [isOpen, results, highlightedIndex, handleSelectUser]
  );

  const showDropdown = isOpen && (results.length > 0 || isLoading);

  return (
    <div className={`user-search ${error ? 'user-search--error' : ''}`} ref={containerRef}>
      {label && (
        <label className="user-search__label">
          {label}
          {required && <span className="user-search__required">*</span>}
        </label>
      )}

      {/* Selected user display */}
      {value ? (
        <div className="user-search__selected">
          <div className="user-search__selected-info">
            <span className="user-search__selected-name">{value.name}</span>
            <span className="user-search__selected-details">
              {value.email} | {value.department}
              {value.location && ` | ${value.location}`}
            </span>
          </div>
          {!disabled && (
            <button
              type="button"
              className="user-search__clear-btn"
              onClick={handleClear}
              aria-label="Clear selection"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M12 4L4 12M4 4l8 8"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          )}
        </div>
      ) : (
        <div className="user-search__input-wrapper">
          <div className="user-search__icon">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M7.333 12.667A5.333 5.333 0 107.333 2a5.333 5.333 0 000 10.667zM14 14l-2.9-2.9"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <input
            ref={inputRef}
            type="text"
            className="user-search__input"
            value={query}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            aria-expanded={showDropdown}
            aria-autocomplete="list"
            aria-controls="user-search-results"
          />
          {isLoading && (
            <div className="user-search__loading">
              <div className="user-search__spinner" />
            </div>
          )}
        </div>
      )}

      {/* Dropdown results */}
      {showDropdown && (
        <ul
          id="user-search-results"
          className="user-search__dropdown"
          role="listbox"
        >
          {isLoading && results.length === 0 ? (
            <li className="user-search__dropdown-loading">Searching...</li>
          ) : (
            results.map((user, index) => (
              <li
                key={user.id}
                className={`user-search__result ${
                  index === highlightedIndex ? 'user-search__result--highlighted' : ''
                }`}
                role="option"
                aria-selected={index === highlightedIndex}
                onClick={() => handleSelectUser(user)}
                onMouseEnter={() => setHighlightedIndex(index)}
              >
                <div className="user-search__result-avatar">
                  {user.name.charAt(0).toUpperCase()}
                </div>
                <div className="user-search__result-info">
                  <span className="user-search__result-name">{user.name}</span>
                  <span className="user-search__result-email">{user.email}</span>
                </div>
                <div className="user-search__result-meta">
                  <span className="user-search__result-dept">{user.department}</span>
                  {user.location && (
                    <span className="user-search__result-location">{user.location}</span>
                  )}
                </div>
              </li>
            ))
          )}
        </ul>
      )}

      {error && <span className="user-search__error">{error}</span>}
    </div>
  );
};
